// My Mail — a My Desk panel (owner, 2026-09-10 / "build it" 2026-09-18;
// redesigned 2026-10-11 after "My Mail did not work for him"): the
// signed-in person's own inbox, read-only.
//
// Layout: unread first (server order), sender bold when unread, subject,
// Gmail's one-line preview, relative time. The header line carries the
// Gmail mark, the unread count, "Open Gmail" and the reload circle. Every
// row opens that thread in Gmail FOR THE SIGNED-IN ACCOUNT (the server
// builds the link with the same `authuser` address as the nav's Gmail mark,
// Nav.tsx gmailUrlFor) — never whichever Google account the browser happens
// to default to.
//
// States, all named, never blank: loading, viewing-as (the server refuses
// with 403 — P2, `src/my_google.js` — told apart by its exact shared
// literal `VIEWING_AS_BLOCKED_MESSAGE`), fault, not connected, empty, and a
// partial read (`missing` > 0) said out loud under the list.
import type { JSX } from 'preact';
import { useResource } from '../lib/store';
import { formatDateTime } from '../lib/date';
import { session } from '../lib/session';
import {
  MY_MAIL_KEY, fetchMyMail, reloadMyMail, relativeTime, VIEWING_AS_BLOCKED_MESSAGE,
  type MyMailData, type MyMailThread,
} from '../lib/myGoogle';
import { gmailUrlFor } from './Nav';
import { DeskPanel } from './DeskPanel';
import { FaultState } from './FaultState';
import { ReloadButton } from './ReloadButton';

function MailRow({ t, now }: { t: MyMailThread; now: Date }): JSX.Element {
  const when = relativeTime(t.receivedAt, now);
  let full = '';
  try { full = t.receivedAt ? formatDateTime(t.receivedAt) : ''; } catch { full = ''; }
  return (
    <li>
      <a
        class={`mymail-row${t.unread ? ' mymail-unread' : ''}`}
        href={t.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`my-mail-row-${t.id}`}
        aria-label={`${t.unread ? 'Unread: ' : ''}${t.from}, ${t.subject || '(no subject)'}, ${when}. Opens in Gmail.`}
      >
        <span class="mymail-line">
          {t.unread ? <span class="mymail-dot" aria-hidden="true" /> : null}
          <span class="mymail-from" data-testid="my-mail-from">{t.from || '(unknown sender)'}</span>
          {t.messageCount > 1 ? <span class="mymail-count">{t.messageCount}</span> : null}
          <span class="mymail-time" title={full}>{when}</span>
        </span>
        <span class="mymail-subject">{t.subject || '(no subject)'}</span>
        {t.snippet ? <span class="mymail-snippet">{t.snippet}</span> : null}
      </a>
    </li>
  );
}

export function MyMailPanel(
  { chromeless = false }: { chromeless?: boolean } = {},
): JSX.Element {
  const load = useResource<MyMailData>(MY_MAIL_KEY, fetchMyMail);
  const gmailHref = gmailUrlFor(session.value.me?.email ?? null);

  const logo = <span class="gmail-mark" role="img" aria-label="Gmail" data-testid="my-mail-logo" />;
  const openGmail = (
    <a class="mymail-open" href={gmailHref} target="_blank" rel="noopener noreferrer" data-testid="my-mail-open-gmail">
      Open Gmail
    </a>
  );
  const toolbar = (count: JSX.Element | null): JSX.Element => (
    <ReloadButton
      label="Reload mail"
      testId="my-mail-refresh"
      leading={<span class="panel-lead mymail-lead">{logo}{count}{openGmail}</span>}
      onClick={() => { void reloadMyMail(); }}
    />
  );
  const wrap = (body: JSX.Element, subtitle?: string): JSX.Element => (
    <DeskPanel title="My Mail" subtitle={subtitle} testId="my-mail-panel" chromeless={chromeless} className="mymail">
      {body}
    </DeskPanel>
  );

  if (load.status === 'loading') {
    return wrap(<p class="muted mymail-state" data-testid="my-mail-loading">Loading your inbox.</p>);
  }

  if (load.status === 'fault') {
    if (load.error === VIEWING_AS_BLOCKED_MESSAGE) {
      return wrap(<p class="muted mymail-state" data-testid="my-mail-viewing-as">Hidden while you are viewing as someone else.</p>);
    }
    return wrap(
      <>
        {toolbar(null)}
        <FaultState name="my_mail_load_failed" detail={`${load.error} — Gmail did not answer. Try the reload circle, or open Gmail directly.`} />
      </>,
    );
  }

  const { data } = load;

  if (!data.connected) {
    return wrap(
      <>
        {toolbar(null)}
        <p class="muted mymail-state" data-testid="my-mail-not-connected">
          Mail is not connected for this account. Your inbox opens with Open Gmail.
        </p>
      </>,
    );
  }

  const unread = typeof data.unreadTotal === 'number' ? data.unreadTotal : null;
  const subtitle = unread === null ? undefined : unread === 0 ? 'All read' : `${unread} unread`;
  const count = subtitle
    ? <span class={`mymail-unread-count${unread ? ' mymail-has-unread' : ''}`} data-testid="my-mail-unread">{subtitle}</span>
    : null;

  if (!data.threads.length) {
    return wrap(
      <>
        {toolbar(count)}
        <p class="muted mymail-state" data-testid="my-mail-empty">Your inbox is empty.</p>
      </>,
      subtitle,
    );
  }

  const now = new Date();
  const missing = data.missing ?? 0;
  return wrap(
    <>
      {toolbar(count)}
      <ul class="mymail-list" data-testid="my-mail-queue">
        {data.threads.map((t) => <MailRow key={t.id} t={t} now={now} />)}
      </ul>
      {missing > 0 ? (
        <p class="muted mymail-state" data-testid="my-mail-missing">
          {missing === 1 ? '1 message' : `${missing} messages`} could not be loaded. Reload to try again.
        </p>
      ) : null}
    </>,
    subtitle,
  );
}
